<script setup lang="ts">
  import { useField, useForm } from 'vee-validate'
  import { toTypedSchema } from '@vee-validate/zod'
  import * as zod from 'zod'
  import { useI18n } from 'vue-i18n'
  import type { ButtonBranding } from '~/types/branding'
  import type { GenericApiError } from '~/types/apiError'
  import type { CanRefer } from '~/types/referral'

  defineProps<{
    buttonBranding: ButtonBranding
  }>()

  const emit = defineEmits<{
    'can-refer': [{ email: string; referralId: number | undefined }]
  }>()

  const { t, te } = useI18n()
  const validationSchema = toTypedSchema(
    zod.object({
      email: zod
        .email({
          error: (issue) =>
            !issue.input
              ? t('SectionReferral.NotAuthenticatedForm.emailEmpty')
              : t('SectionReferral.NotAuthenticatedForm.emailError')
        })
        .refine(
          (value) => !value.toLowerCase().includes('@yopmail') && !value.toLowerCase().includes('+'),
          t('SectionReferral.NotAuthenticatedForm.emailError')
        )
    })
  )
  const { handleSubmit, errors, setErrors } = useForm({ validationSchema })
  const { value: email } = useField('email')

  const onSubmit = handleSubmit(async (values) => {
    const { email } = values
    const { canRefer } = useStoreReferral()

    useTrack().track('click-referral-connexion', { email })

    try {
      const response = (await canRefer(email)) as CanRefer
      if (response.can_refer) {
        emit('can-refer', { email, referralId: response.referral_id })
      }
    } catch (error) {
      const errors = Object.values(error as GenericApiError)

      if (errors.length === 0) {
        setErrors({ email: t('SectionReferral.NotAuthenticatedForm.emailUnknown') })
        return
      }

      for (const value of errors) {
        const transKey = `ApiErrors.${value}`
        setErrors({ email: te(transKey) ? t(transKey) : t('ApiErrors.generic') })
      }
    }
  })
</script>

<template>
  <form
    class="referral-form"
    @submit.stop="onSubmit"
  >
    <FormInputText
      v-model="email"
      name="email"
      :placeholder="$t('SectionReferral.NotAuthenticatedForm.emailPlaceholder')"
      :error-message="errors.email"
    />
    <ButtonBranding
      :button-branding="buttonBranding"
      type="submit"
      @click="handleSubmit"
    >
      {{ buttonBranding.text }}
    </ButtonBranding>
  </form>
</template>

<style scoped>
  .referral-form {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
</style>
